/*
 * Gestion de l'authentification et de la connexion utilisateur (flux Option B)
 *
 * Le token revient directement dans la réponse de /login (grant OAuth2 "password").
 * Plus de tunnel SSE ni de clientId : un simple aller-retour.
 *   1. Envoi des identifiants à /login
 *   2. Lecture de l'access token dans la réponse
 *   3. Récupération des cookies (refresh + CSRF) puis du profil
 */

import { NavigateFunction } from "react-router-dom";
import { RequestTypeOf } from "../generated/routes";
import { I18nKey } from "../i18n/i18n";
import { fetchWithToken } from "../utils/fetchWithToken";
import { apiFetchRoute } from "./apiFetchRoute";
import { fetchAccountStorage } from "./profile";

// Type pour la réponse du token
export type TokenResponse = {
  accessToken: string;
};

export type LoginError =
  | "invalidCredentials"
  | "serverError"
  | "refreshTokenError"
  | "accountStorageError";

export const loginErrorToI18nKey: Record<LoginError, I18nKey> = {
  // i18n.t("login-errors-invalid-credentials")
  invalidCredentials: "login-errors-invalid-credentials",
  // i18n.t("login-errors-server")
  serverError: "login-errors-server",
  // i18n.t("login-errors-refresh-token")
  refreshTokenError: "login-errors-refresh-token",
  // i18n.t("login-errors-account-storage")
  accountStorageError: "login-errors-account-storage",
};

export const handleSubmit = async (
  e: React.FormEvent<HTMLFormElement>,
  email: string,
  password: string,
  setError: (error: LoginError | null) => void,
  setIsLoading: (isLoading: boolean) => void,
  setIsConnected: (isConnected: boolean) => void,
  isConnected: boolean,
  navigate: NavigateFunction
) => {
  e.preventDefault();
  setError(null);
  setIsConnected(false);
  setIsLoading(true);

  try {
    // Type du body inféré depuis la route générée
    type LoginRequest = RequestTypeOf<"postLogin">;

    const body: LoginRequest = { email, password };

    // 1. Envoi des identifiants — le token est renvoyé dans la réponse
    const loginResponse = await apiFetchRoute("postLogin", body);

    if (!loginResponse.ok) {
      setError("invalidCredentials");
      setIsLoading(false);
      return;
    }

    // 2. Lecture de l'access token depuis la réponse
    const receivedToken = (await loginResponse.json()) as TokenResponse;
    if (!receivedToken?.accessToken) {
      setError("serverError");
      setIsLoading(false);
      return;
    }
    localStorage.setItem("accessToken", receivedToken.accessToken);
    setIsConnected(true);

    // 3. Récupération des cookies (refresh + CSRF)
    const refreshCookieResponse = await fetchWithToken(
      `${process.env.REACT_APP_URL_SERVER_DATA}/get-cookies`,
      {
        method: "POST",
      }
    );

    if (!refreshCookieResponse.ok) {
      setError("refreshTokenError");
      setIsLoading(false);
      return;
    }

    // 4. Récupérer les données utilisateur
    try {
      await fetchAccountStorage();
    } catch (err) {
      setError("accountStorageError");
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    navigate("/");
  } catch (error) {
    setError("serverError");
    setIsLoading(false);
  }
};
